import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import { conditionalApiRequest, checkoutApiRequest, API_CONFIG } from '../config/api';
import { handleApiError } from '../utils/errorHandler';
import { cartService } from '../services/cartService';
import './Checkout.css';

const Checkout: React.FC = () => {
  const [paymentMethod, setPaymentMethod] = useState('paystack');
  const [agree, setAgree] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [couponLoading, setCouponLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSummary, setOrderSummary] = useState<any>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [itemCount, setItemCount] = useState(0);
  const [cartLoading, setCartLoading] = useState(false);
  const [hasCouponProducts, setHasCouponProducts] = useState(false);

  const navigate = useNavigate();
  const { addToast, showSuccess, showError } = useToast();
  const summarySubtotalNgn = orderSummary?.subtotal_ngn ?? orderSummary?.subtotal ?? 0;
  const summaryDiscountNgn = orderSummary?.discount_ngn ?? orderSummary?.discount_amount ?? orderSummary?.discount ?? 0;
  const summaryShippingNgn = orderSummary?.shipping_cost_ngn ?? orderSummary?.shipping_cost ?? 0;
  const summaryTotalNgn = orderSummary?.total_ngn ?? orderSummary?.total_amount ?? orderSummary?.total ?? 0;
  const hasOrderSummary = !!orderSummary && (
    orderSummary.subtotal_ngn !== undefined ||
    orderSummary.total_ngn !== undefined ||
    orderSummary.subtotal !== undefined ||
    orderSummary.total !== undefined
  );
  const formatNgn = (amount: number) => `₦${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch the backend-calculated order summary.
  const fetchOrderSummary = async (couponCode?: string) => {
    try {
      setSummaryLoading(true);
      const summaryData = await cartService.getCartSummary({
        ...(couponCode ? { coupon_code: couponCode } : {}),
        payment_method: 'paystack',
      });
      if (summaryData && (
        summaryData.total_ngn !== undefined ||
        summaryData.subtotal_ngn !== undefined ||
        summaryData.total_amount !== undefined ||
        summaryData.subtotal !== undefined
      )) {
        // Only update if the summary has items, otherwise keep the fallback from fetchCart
        if (summaryData.total_items === undefined || summaryData.total_items > 0) {
          setOrderSummary(summaryData);
        }
      }
    } catch (error) {
    } finally {
      setSummaryLoading(false);
    }
  };

  // Fetch actual cart items and fallback summary
  const fetchCart = async () => {
    try {
      setCartLoading(true);
      const cartData = await cartService.getCart();
      const products = cartData.products || [];

      const transformedProducts = products.map((product: any) => ({
        id: product.id,
        name: product.name,
        slug: product.slug,
        price: parseFloat(product.discounted_price || product.price) || 0,
        quantity: product.quantity || 1,
        item_total: product.subtotal || (parseFloat(product.discounted_price || product.price) || 0) * product.quantity,
        brand: product.brand_name || product.brand,
        image: product.main_image,
        usdPrice: parseFloat(product.price_usdt) || undefined,
        discount: product.discount_percentage || undefined,
      }));

      setCartItems(transformedProducts);
      setItemCount(cartData.total_items || 0);

      // Set order summary from cart data as fallback
      setOrderSummary((prev: any) => ({
        ...prev,
        subtotal: cartData.total_amount || 0,
        total: cartData.total_amount || 0,
        total_usdt: cartData.total_amount_usdt || 0,
        total_items: cartData.total_items || 0,
      }));
    } catch (error) {
      setCartItems([]);
      setItemCount(0);
    } finally {
      setCartLoading(false);
    }
  };

  // Fetch summary on initial load
  useEffect(() => {
    const load = async () => {
      await fetchCart();
      await fetchOrderSummary();
    };
    load();
  }, []);

  // Re-fetch when payment method changes (if state already entered)
  useEffect(() => {
    if (formData.state.trim()) {
      fetchOrderSummary(appliedCoupon?.code || couponCode || undefined);
    }
  }, [paymentMethod]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      showError('Validation Error', 'Please enter a coupon code');
      return;
    }

    if (!formData.email) {
      showError('Validation Error', 'Please enter your email address first');
      return;
    }

    setCouponLoading(true);

    try {
      // Get cart token for guest users
      const cartToken = cartService.getCartToken();
      
      const response = await conditionalApiRequest<any>(API_CONFIG.ENDPOINTS.COUPONS_VALIDATE, {
        method: 'POST',
        body: JSON.stringify({
          coupon_code: couponCode.trim(),
          order_amount: orderSummary?.total || orderSummary?.total_amount || orderSummary?.total_ngn || 0
        })
      });

      if (response.success) {
        const couponData = response.data || response;
        // Store coupon data with all details
        setAppliedCoupon({
          code: couponData.code,
          discount_type: couponData.discount_type,
          discount_value: couponData.discount_value,
          discount_amount: couponData.discount_amount,
          final_amount: couponData.final_amount
        });
        
        // Format discount message based on type
        const discountText = couponData.discount_type === 'percentage' 
          ? `${couponData.discount_value}% off` 
          : `₦${couponData.discount_amount?.toLocaleString()} off`;
        
        showSuccess('Coupon Applied!', `You saved ${discountText} on your order!`);

        // Now fetch the updated order summary with the coupon code applied
        try {
          
          const summaryData = await cartService.getCartSummary({
            ...(formData.state ? { state: formData.state } : {}),
            payment_method: paymentMethod,
            coupon_code: couponCode.trim(),
          });
          
          if (summaryData) {
            // Update order summary with discount information
            setOrderSummary((prev: any) => ({
              ...prev,
              ...summaryData,
              coupon_applied: true,
              applied_coupon_code: couponCode.trim()
            }));
          }
        } catch (summaryError) {
          // Continue anyway - the discount will still be shown from the coupon response
          setOrderSummary((prev: any) => ({
            ...prev,
            coupon_applied: true,
            applied_coupon_code: couponCode.trim(),
            discount_amount: response.discount_amount,
            discount: response.discount_amount
          }));
        }

      } else {
        showError('Invalid Coupon', response.message || 'This coupon code is not valid or has expired');
        setAppliedCoupon(null);
      }
    } catch (error: any) {
      const errorMessage = handleApiError(error, 'Coupon Validation');
      showError('Coupon Validation Failed', errorMessage);
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = async () => {
    setAppliedCoupon(null);
    setCouponCode('');

    try {
      // Fetch the original order summary without coupon
      const summaryData = await cartService.getCartSummary({
        ...(formData.state ? { state: formData.state } : {}),
        payment_method: paymentMethod,
      });
      
      if (summaryData) {
        setOrderSummary((prev: any) => ({
          ...prev,
          ...summaryData,
          coupon_applied: false,
          applied_coupon_code: null,
          discount_amount: 0,
          discount: 0
        }));
      }
    } catch (error) {
      // Restore original total manually
      setOrderSummary((prev: any) => ({
        ...prev,
        discount: 0,
        discount_amount: 0,
        coupon_applied: false,
        applied_coupon_code: null,
      }));
    }

    showSuccess('Coupon removed');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
  };

  const handleOrder = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!agree) return;

    setIsSubmitting(true);

    try {
      // Validate cart is not empty (use actual cart items as source of truth)
      const effectiveItemCount = itemCount || orderSummary?.total_items || cartItems.length || 0;
      if (effectiveItemCount === 0) {
        showError('Empty Cart', 'Your cart is empty. Please add items before checking out.');
        setIsSubmitting(false);
        return;
      }

      // Get cart token for guest users
      const cartToken = cartService.getCartToken();
      
      if (!cartToken) {
        showError('Cart Error', 'Unable to identify your cart. Please add items to cart first.');
        setIsSubmitting(false);
        return;
      }
      

      // Use actual order summary data instead of hardcoded values
      const orderPayload: any = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone_number: formData.phoneNumber,
        shipping_address: formData.streetAddress,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postalCode,
        country: formData.country || 'Nigeria',
        payment_method: 'paystack',
        currency: 'NGN',
      };
      if (appliedCoupon?.code) orderPayload.coupon_code = appliedCoupon.code;
      if (cartToken) orderPayload.cart_token = cartToken;

      const result = await conditionalApiRequest<any>('/api/v1/orders/create/', {
        method: 'POST',
        body: JSON.stringify(orderPayload)
      });


      const createdOrder = result || {};
      const orderId = createdOrder.order_id;

      if (!orderId) {
        showError('Error creating order', 'Order creation failed');
        return;
      }

      showSuccess('Order created!', 'Initiating payment...');

      // Paystack: call payments/create/ and redirect to authorization_url
      const payRes = await conditionalApiRequest<any>('/api/v1/payments/create/', {
        method: 'POST',
        body: JSON.stringify({
          order_id: orderId,
          amount: createdOrder.total,
          currency: 'NGN',
          email: formData.email,
        })
      });
      const payData = payRes.data || payRes;
      if (payData.authorization_url) {
        window.location.href = payData.authorization_url;
      } else {
        showError('Payment Error', 'Could not get Paystack payment URL');
      }
    } catch (error) {
      const errorMessage = handleApiError(error as any, 'Order Submission');
      showError('Error submitting order', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="checkout-container">
      <div className="checkout-header">
        <a href="/cart" className="back-link">&larr; Back to Cart</a>
        <h1>Checkout</h1>
        <p className="checkout-subtitle">Complete your purchase securely and safely</p>
      </div>
      <div className="checkout-content">
        {isMobile && (
          <div className="checkout-summary">
            <div className="summary-box">
              <div className="summary-title">Order Summary</div>
              <div className="coupon-row">
                <input
                  type="text"
                  placeholder="Enter coupon code"
                  className="coupon-input"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  disabled={!!appliedCoupon}
                />
                {appliedCoupon ? (
                  <button className="apply-btn remove" onClick={handleRemoveCoupon}>
                    Remove
                  </button>
                ) : (
                  <button
                    className="apply-btn"
                    onClick={handleApplyCoupon}
                    disabled={couponLoading || !couponCode.trim()}
                  >
                    {couponLoading ? 'Applying...' : 'Apply'}
                  </button>
                )}
              </div>
              {appliedCoupon && (
                <div className="applied-coupon">
                  <div className="coupon-success">
                    <span className="coupon-check">✓</span>
                    <span className="coupon-text">
                      {appliedCoupon.code}: {appliedCoupon.discount_type === 'percentage' ? `${appliedCoupon.discount_value}%` : `₦${appliedCoupon.discount_value?.toLocaleString()}`} off
                    </span>
                  </div>
                  <div className="coupon-details">
                    <div className="coupon-detail-row">
                      <span>Discount:</span>
                      <span className="discount-amount">-₦{appliedCoupon.discount_amount?.toLocaleString()}</span>
                    </div>
                    <div className="coupon-detail-row final">
                      <span>Final Amount:</span>
                      <span className="final-amount">₦{appliedCoupon.final_amount?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}
              <div className="summary-details">
                {summaryLoading ? (
                  <div className="summary-loading">Loading order summary...</div>
                ) : hasOrderSummary ? (
                  <>
                    <div className="summary-row">
                      <span>Subtotal ({orderSummary.items_count || orderSummary.total_items || 0} items)</span>
                      <span>
                        {formatNgn(summarySubtotalNgn)}
                      </span>
                    </div>

                    {orderSummary.coupon_applied && summaryDiscountNgn > 0 && (
                      <>
                        <div style={{ borderTop: '1px solid #e0e0e0', margin: '12px 0', paddingTop: '12px' }}>
                          <div className="summary-row discount-row">
                            <span style={{ color: '#10b981', fontWeight: '600' }}>Discount:</span>
                            <span style={{ color: '#10b981', fontWeight: '700' }}>
                              -{formatNgn(summaryDiscountNgn)}
                            </span>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="summary-row">
                      <span>Shipping</span>
                      <span>
                        {formatNgn(summaryShippingNgn)}
                      </span>
                    </div>

                    <div className="summary-row total-row" style={{ fontWeight: '700', fontSize: '1.2rem', color: '#059669', borderTop: '1px solid #e0e0e0', paddingTop: '12px', marginTop: '12px' }}>
                      <span>Total</span>
                      <span>
                        {formatNgn(summaryTotalNgn)}
                      </span>
                    </div>
                    
                    {orderSummary.note && (
                      <div className="summary-row note" style={{ fontSize: '0.85rem', color: '#666', marginTop: '8px' }}>
                        <span>{orderSummary.note}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="summary-error">Unable to load order summary</div>
                )}
              </div>
              
              {/* Coupon notice */}
              {hasCouponProducts && (
                <div className="coupon-notice">
                  <div className="coupon-notice-icon">🎁</div>
                  <div className="coupon-notice-text">
                    <strong>Gift Coupon Purchase</strong><br/>
                    Your coupon code will be sent to your email after successful payment.
                  </div>
                </div>
              )}
              
              <div className="shipping-info">
                <div className="shipping-title">Shipping Information</div>
                <ul>
                  <li>Lagos: 1-3 business days</li>
                  <li>Other states: 4-5 business days</li>
                </ul>
              </div>
              <div className="trust-badges">
                <span>Secure Payment</span>
                <span>Authentic Products</span>
                <span>7-Day Returns</span>
                <span>24/7 Support</span>
              </div>
            </div>
          </div>
        )}
        <form className="checkout-form" onSubmit={handleOrder} autoComplete="off">
          {/* Customer Information */}
          <section className="checkout-section">
            <div className="section-title"><span className="section-number">1</span> Customer Information</div>
            <div className="form-row">
              <input
                type="text"
                name="firstName"
                placeholder="First Name *"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                autoComplete="off"
              />
              <input
                type="text"
                name="lastName"
                placeholder="Last Name *"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                autoComplete="off"
              />
            </div>
            <div className="form-row">
              <input
                type="email"
                name="email"
                placeholder="Email *"
                value={formData.email}
                onChange={handleInputChange}
                required
                autoComplete="off"
              />
              <input
                type="tel"
                name="phoneNumber"
                placeholder="Phone Number *"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                required
                autoComplete="off"
              />
            </div>
          </section>

          {/* Shipping Address */}
          <section className="checkout-section">
            <div className="section-title"><span className="section-number">2</span> Shipping Address</div>
            <input
              type="text"
              name="streetAddress"
              placeholder="Street Address *"
              className="full-width"
              value={formData.streetAddress}
              onChange={handleInputChange}
              required
              autoComplete="off"
            />
            <div className="form-row">
              <input
                type="text"
                name="city"
                placeholder="City *"
                value={formData.city}
                onChange={handleInputChange}
                required
                autoComplete="off"
              />
              <input
                type="text"
                name="state"
                placeholder="State *"
                value={formData.state}
                onChange={handleInputChange}
                required
                autoComplete="off"
              />
            </div>
            <div className="form-row">
              <input
                type="text"
                name="postalCode"
                placeholder="Postal Code"
                value={formData.postalCode}
                onChange={handleInputChange}
                autoComplete="off"
              />
              <input
                type="text"
                name="country"
                placeholder="Country"
                value={formData.country}
                onChange={handleInputChange}
                autoComplete="off"
              />
            </div>
          </section>

          {/* Payment Method */}
          <section className="checkout-section">
            <div className="section-title"><span className="section-number">3</span> Payment Method</div>
            <div className="payment-options">
              <label className={`payment-option${paymentMethod === 'paystack' ? ' selected' : ''}`}>
                <input type="radio" name="payment" value="paystack" checked={paymentMethod === 'paystack'} onChange={() => setPaymentMethod('paystack')} />
                <div>
                  <div>Paystack (Card / Bank)</div>
                  <small>Pay with debit card or bank transfer via Paystack</small>
                </div>
              </label>
            </div>
          </section>

          {/* Additional Information */}
          <section className="checkout-section">
            <div className="section-title"><span className="section-number">4</span> Additional Information</div>
            <label className="special-label">Special Instructions (Optional)</label>
            <textarea
              className="special-textarea"
              placeholder="Special Instructions (Optional)"
              value={specialInstructions}
              onChange={e => setSpecialInstructions(e.target.value)}
              rows={3}
            />
            <div className="info-badges-row">
              <div className="info-badge secure">
                <div className="info-badge-title">Secure Checkout</div>
                <div className="info-badge-desc">SSL encrypted</div>
              </div>
              <div className="info-badge delivery">
                <div className="info-badge-title">Fast Delivery</div>
                <div className="info-badge-desc">2-5 business days</div>
              </div>
              <div className="info-badge support">
                <div className="info-badge-title">24/7 Support</div>
                <div className="info-badge-desc">WhatsApp & Telegram</div>
              </div>
            </div>

            {/* WhatsApp Help Section */}
            <div className="whatsapp-help-section">
              <div className="whatsapp-help-title">Need Help?</div>
              <p className="whatsapp-help-text">Get instant assistance from our support team</p>
              <button
                type="button"
                className="whatsapp-help-btn"
                onClick={() => {
                  const message = "Hi, I need help with my order during checkout.";
                  const whatsappUrl = `https://api.whatsapp.com/send?phone=2349138666111&text=${encodeURIComponent(message)}`;
                  window.open(whatsappUrl, '_blank');
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                Get Help by WhatsApp
              </button>
            </div>
            <div className="checkbox-row">
              <label className="checkbox-label">
                <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} required />
                I agree to the <a href="#">Terms and Conditions</a> and <a href="#">Privacy Policy</a> *
              </label>
              <label className="checkbox-label">
                <input type="checkbox" checked={newsletter} onChange={e => setNewsletter(e.target.checked)} />
                Subscribe to our newsletter for exclusive deals and updates
              </label>
            </div>
            
            {/* Mobile Checkout Summary - appears before checkout button */}
            {isMobile && (
              <div className="mobile-checkout-summary">
                <div className="summary-box">
                  <div className="summary-title">Order Summary</div>
                  <div className="coupon-row">
                    <input
                      type="text"
                      placeholder="Enter coupon code"
                      className="coupon-input"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      disabled={!!appliedCoupon}
                    />
                    {appliedCoupon ? (
                      <button className="apply-btn remove" onClick={handleRemoveCoupon}>
                        Remove
                      </button>
                    ) : (
                      <button
                        className="apply-btn"
                        onClick={handleApplyCoupon}
                        disabled={couponLoading || !couponCode.trim()}
                      >
                        {couponLoading ? 'Applying...' : 'Apply'}
                      </button>
                    )}
                  </div>
                  {appliedCoupon && (
                    <div className="applied-coupon">
                      <div className="coupon-success">
                        <span className="coupon-check">✓</span>
                        <span className="coupon-text">
                          {appliedCoupon.code}: {appliedCoupon.discount_type === 'percentage' ? `${appliedCoupon.discount_value}%` : `₦${appliedCoupon.discount_value?.toLocaleString()}`} off
                        </span>
                      </div>
                      <div className="coupon-details">
                        <div className="coupon-detail-row">
                          <span>Discount:</span>
                          <span className="discount-amount">-₦{appliedCoupon.discount_amount?.toLocaleString()}</span>
                        </div>
                        <div className="coupon-detail-row final">
                          <span>Final Amount:</span>
                          <span className="final-amount">₦{appliedCoupon.final_amount?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  )}
                    <div className="summary-details">
                      {summaryLoading ? (
                        <div className="summary-loading">Loading order summary...</div>
                      ) : hasOrderSummary ? (
                        <>
                          <div className="summary-row">
                            <span>Subtotal ({orderSummary.items_count || orderSummary.total_items || 0} items)</span>
                            <span>
                              {formatNgn(summarySubtotalNgn)}
                            </span>
                          </div>

                          {orderSummary.coupon_applied && summaryDiscountNgn > 0 && (
                            <div className="summary-row" style={{ color: '#10b981' }}>
                              <span>Discount</span>
                              <span>
                                -{formatNgn(summaryDiscountNgn)}
                              </span>
                            </div>
                          )}

                          <div className="summary-row">
                            <span>Shipping</span>
                            <span>{formatNgn(summaryShippingNgn)}</span>
                          </div>

                          <div className="summary-row total-row" style={{ fontWeight: '700', fontSize: '1.2rem', color: '#059669', borderTop: '1px solid #e0e0e0', paddingTop: '12px', marginTop: '12px' }}>
                            <span>Total</span>
                            <span>
                              {formatNgn(summaryTotalNgn)}
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="summary-error">Unable to load order summary</div>
                      )}
                    </div>
                  
                  {/* Coupon notice */}
                  {hasCouponProducts && (
                    <div className="coupon-notice">
                      <div className="coupon-notice-icon">🎁</div>
                      <div className="coupon-notice-text">
                        <strong>Gift Coupon Purchase</strong><br/>
                        Your coupon code will be sent to your email after successful payment.
                      </div>
                    </div>
                  )}
                  
                  <div className="shipping-info">
                    <div className="shipping-title">Shipping Information</div>
                    <ul>
                      <li>Lagos: 1-3 business days</li>
                      <li>Other states: 4-5 business days</li>
                    </ul>
                  </div>
                  <div className="trust-badges">
                    <span>Secure Payment</span>
                    <span>Authentic Products</span>
                    <span>7-Day Returns</span>
                    <span>24/7 Support</span>
                  </div>
                </div>
              </div>
            )}
            
            <button
              className="complete-order-btn"
              type="submit"
              disabled={!agree || isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'Complete Order'}
            </button>
          </section>
        </form>
        {!isMobile && (
          <div className="checkout-summary">
            <div className="summary-box">
              <div className="summary-title">Order Summary</div>
              <div className="coupon-row">
                <input
                  type="text"
                  placeholder="Enter coupon code"
                  className="coupon-input"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  disabled={!!appliedCoupon}
                />
                {appliedCoupon ? (
                  <button className="apply-btn remove" onClick={handleRemoveCoupon}>
                    Remove
                  </button>
                ) : (
                  <button
                    className="apply-btn"
                    onClick={handleApplyCoupon}
                    disabled={couponLoading || !couponCode.trim()}
                  >
                    {couponLoading ? 'Applying...' : 'Apply'}
                  </button>
                )}
              </div>
              {appliedCoupon && (
                <div className="applied-coupon">
                  <div className="coupon-success">
                    <span className="coupon-check">✓</span>
                    <span className="coupon-text">
                      {appliedCoupon.code}: {appliedCoupon.discount_type === 'percentage' ? `${appliedCoupon.discount_value}%` : `₦${appliedCoupon.discount_value?.toLocaleString()}`} off
                    </span>
                  </div>
                  <div className="coupon-details">
                    <div className="coupon-detail-row">
                      <span>Discount:</span>
                      <span className="discount-amount">-₦{appliedCoupon.discount_amount?.toLocaleString()}</span>
                    </div>
                    <div className="coupon-detail-row final">
                      <span>Final Amount:</span>
                      <span className="final-amount">₦{appliedCoupon.final_amount?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}
              <div className="summary-details">
                {summaryLoading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>
                    Loading order summary...
                  </div>
                ) : hasOrderSummary ? (
                  <>
                    <div className="summary-row">
                      <span>Subtotal ({orderSummary.items_count || orderSummary.total_items || 0} items)</span>
                      <span>
                        {formatNgn(summarySubtotalNgn)}
                      </span>
                    </div>

                    {orderSummary.coupon_applied && summaryDiscountNgn > 0 && (
                      <div className="summary-row discount-row">
                        <span style={{ color: '#10b981' }}>Discount</span>
                        <span style={{ color: '#10b981' }}>
                          -{formatNgn(summaryDiscountNgn)}
                        </span>
                      </div>
                    )}

                    <div className="summary-row">
                      <span>Shipping</span>
                      <span>{formatNgn(summaryShippingNgn)}</span>
                    </div>

                    <div className="summary-row total-row" style={{ fontWeight: '700', fontSize: '1.2rem', color: '#059669', borderTop: '1px solid #e0e0e0', paddingTop: '12px', marginTop: '12px' }}>
                      <span>Total</span>
                      <span>
                        {formatNgn(summaryTotalNgn)}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span>₦0.00</span>
                  </div>
                )}
              </div>
              
              {/* Coupon notice */}
              {hasCouponProducts && (
                <div className="coupon-notice">
                  <div className="coupon-notice-icon">🎁</div>
                  <div className="coupon-notice-text">
                    <strong>Gift Coupon Purchase</strong><br/>
                    Your coupon code will be sent to your email after successful payment.
                  </div>
                </div>
              )}
              
              <div className="shipping-info">
                <div className="shipping-title">Shipping Information</div>
                <ul>
                  <li>Lagos: 1-3 business days</li>
                  <li>Other states: 4-5 business days</li>
                </ul>
              </div>
              <div className="trust-badges">
                <span>Secure Payment</span>
                <span>Authentic Products</span>
                <span>7-Day Returns</span>
                <span>24/7 Support</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Checkout;


